import { atom } from '@reatom/framework'
import * as DateF from 'date-fns'
import { match } from 'ts-pattern'

import { AsyncStateN, Duration, pipe, Result } from '@blade/std-lib'
import { Trend } from '@root/entities/trend'
import { DATE_FORMAT, DomUtil, Error } from '@root/shared'

import type { Ctx } from '@reatom/framework'

/** Fixed 2x scale for consistent cross-platform canvas output (see CanvasPnlBuilder). */
export const CANVAS_RENDER_SCALE = 2

export abstract class CanvasPnlBuilderTemplate {
  protected abstract renderAll: () => void

  public canvas!: HTMLCanvasElement
  public state = atom(
    AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
    'canvas-pnl-builder-state',
  )
  protected ctx!: CanvasRenderingContext2D
  protected isPnlPositive: boolean

  protected reatomCtx: Ctx
  private positiveColor: string = '#50FF03'
  private negativeColor: string = '#EE3544'
  private neutralColor: string = '#b4b4b4'
  private timeout = Duration.minutes(1)
  private positiveImage: string
  private negativeImage: string
  private bgWidth: number
  private bgRatio: number

  /**
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @param {TradingPosition.TradingPosition} position - The trading position
   */
  constructor(
    isPnlPositive: boolean,
    reatomCtx: Ctx,
    positiveImage: string,
    negativeImage: string,
    bgWidth: number,
    bgRatio: number,
  ) {
    this.reatomCtx = reatomCtx
    this.isPnlPositive = isPnlPositive
    this.positiveImage = positiveImage
    this.negativeImage = negativeImage
    this.bgWidth = bgWidth
    this.bgRatio = bgRatio
  }

  public setCanvas(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      this.state(
        this.reatomCtx,
        AsyncStateN.error(
          Error.UnknownError.fromUnknown('Canvas context not found'),
        ),
      )
      return this
    }

    this.ctx = ctx
    this.canvas = canvas

    return this
  }

  public async build() {
    this.canvas.width = this.bgWidth
    this.canvas.height = this.bgWidth * this.bgRatio

    this.state(this.reatomCtx, AsyncStateN.loading())

    await this.connectFonts()

    const imgResult = await DomUtil.loadImage(
      this.isPnlPositive ? this.positiveImage : this.negativeImage,
      this.timeout,
    )

    if (Result.isSuccess(imgResult)) {
      this.ctx.drawImage(
        imgResult.success,
        0,
        0,
        imgResult.success.width,
        imgResult.success.height,
        0,
        0,
        this.canvas.width,
        this.canvas.height,
      )

      await this.renderAll()
    }

    this.state(
      this.reatomCtx,
      AsyncStateN.fromResult(
        pipe(
          imgResult,
          Result.map(() => undefined),
          Result.mapError(() =>
            Error.UnknownError.fromUnknown('Failed to load image'),
          ),
        ),
      ),
    )
  }

  public toDataUrl = () => this.canvas.toDataURL()

  public toBlob = (): Promise<Blob | undefined> => {
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        resolve(blob ?? undefined)
      })
    })
  }

  /**
   * @description Render trade date
   * - uses timestamp
   */
  protected renderDate = (x: number, y: number, dateAt: Date) => {
    const time = DateF.format(dateAt, DATE_FORMAT.HH_mm)
    const date = DateF.format(dateAt, DATE_FORMAT.dd_MM_yy)

    this.ctx.save()
    try {
      this.ctx.textAlign = 'left'
      this.ctx.fillStyle = '#FFF'
      this.ctx.font = '32px Inter_SemiBold'
      this.ctx.fillText(date, this.x(x), this.x(y))

      const dateWidth = this.ctx.measureText(date).width + 8

      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.23)'
      this.ctx.fillText(time, this.x(x) + dateWidth, this.x(y))
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description helper method to draw an image with rounded corners on the canvas
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @param {number} r - The radius
   * @param {string} src - The image source
   */
  protected image = (x: number, y: number, r: number, src: string): void => {
    const img = new Image()
    img.src = src
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      this.ctx.save()
      try {
        this.ctx.beginPath()

        if (r === 0) {
          this.ctx.drawImage(img, this.x(x), this.x(y))
        } else {
          this.ctx.arc(
            this.x(x + r),
            this.x(y + r),
            this.x(r),
            0,
            Math.PI * 2,
            true,
          )
          this.ctx.closePath()
          this.ctx.clip()

          this.ctx.drawImage(
            img,
            this.x(x),
            this.x(y),
            this.x(r * 2),
            this.x(r * 2),
          )
        }
      } finally {
        this.ctx.restore()
      }
    }
  }

  /**
   * @description helper method to scales the size by const
   * @param {number} x - The size
   * @returns {number} The size scaled by two
   */
  protected x = (x: number): number => x * CANVAS_RENDER_SCALE
  protected y = (y: number): number => y * CANVAS_RENDER_SCALE

  protected getPnlColor = () =>
    this.isPnlPositive ? this.positiveColor : this.negativeColor
  protected getTrendColor = (trend: Trend.Trend) => {
    return match(trend)
      .with(Trend.Down, () => this.negativeColor)
      .with(Trend.Up, () => this.positiveColor)
      .with(Trend.Neutral, () => this.neutralColor)
      .exhaustive()
  }

  /**
   * @description load Inter fonts to the canvas
   * @link https://developer.mozilla.org/en-US/docs/Web/API/FontFace
   */
  private connectFonts = async () => {
    const fonts = [
      new FontFace(
        'Inter_Regular',
        `url(/fonts/inter-cyrillic-latin-400.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '400',
        },
      ),
      new FontFace(
        'Inter_Medium',
        `url(/fonts/inter-cyrillic-latin-500.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '500',
        },
      ),
      new FontFace(
        'Inter_SemiBold',
        `url(/fonts/inter-cyrillic-latin-600.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '600',
        },
      ),
      new FontFace(
        'Inter_Bold',
        `url(/fonts/inter-cyrillic-latin-700.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '700',
        },
      ),
    ]

    await Promise.all(fonts.map((font) => font.load()))
    for (const font of fonts) {
      document.fonts.add(font)
    }
    await document.fonts.ready
  }
}
